import React, {  useEffect, useMemo, useState } from "react";

import { Box, Text } from "@chakra-ui/react";
import { Chart, useChart } from "@chakra-ui/charts";
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis, ReferenceLine, ReferenceDot } from "recharts";
import * as Cesium from "cesium";
import { useAppStore } from "../../../../../context/store";
const LayersChartCZML = () => {
  // state
  const czmlChart = useAppStore((s) => s.czmlChart);

  // ref
  const viewerRef = useAppStore.getState().viewerRef;

  const [activeIdx, setActiveIdx] = useState(null);

  const chartData = useMemo(() => {
    if (!czmlChart?.chartData?.rows?.length) return null;
    return czmlChart.chartData.rows.map((r, idx) => ({
      idx,
      distance: r.distance_m,
      yaw: r.yaw_rad,
      timeSec: r.timeSec,
      epoch: r.epoch,
    }));
  }, [czmlChart]);

  const chart = useChart({
    data: chartData || [],
    series: [{ name: "yaw", color: "teal.solid" }],
  });

  // sync with cesium timeline
  useEffect(() => {
    if (!viewerRef?.current || !chartData?.length) return;
    const viewer = viewerRef.current;
    const clock = viewer.clock;
    if (!clock) return;

    const epochStr = chartData[0].epoch;
    const epochJD = epochStr ? Cesium.JulianDate.fromIso8601(epochStr) : null;
    if (!epochJD) return;

    const onTick = () => {
      const now = clock.currentTime;
      const sec = Cesium.JulianDate.secondsDifference(now, epochJD);
      let best = 0;
      let bestDiff = Infinity;
      for (let i = 0; i < chartData.length; i++) {
        const d = Math.abs(chartData[i].timeSec - sec);
        if (d < bestDiff) {
          bestDiff = d;
          best = i;
        }
      }
      setActiveIdx(best);
    };

    const remove = clock.onTick.addEventListener(onTick);
    return () => remove();
  }, [viewerRef, chartData]);

  if (!czmlChart) return null;

  const activePoint = activeIdx != null && chartData ? chartData[activeIdx] : null;

  return (
    <Box position="absolute" bottom="35px" right="16px" left="180px" height="30vh" zIndex={1001} bg="white" borderWidth="1px" borderRadius="md" shadow="md" p={3}>
      {!chartData ? (
        <Text fontSize="xs">No chart data.</Text>
      ) : (
        <Chart.Root chart={chart} maxH="full">
          <LineChart data={chart.data} width={320} height={160} margin={{ top: 8, right: 12, left: 0, bottom: 16 }}>
            <CartesianGrid stroke={chart.color("border")} vertical={false} />
            <XAxis type="number" dataKey={chart.key("distance")} stroke={chart.color("border")} tickLine={false} label={{ value: "distance_m", position: "insideBottom", offset: -10 }} />
            <YAxis type="number" stroke={chart.color("border")} tickLine={false} tickMargin={6} label={{ value: "yaw_rad", angle: -90, position: "insideLeft" }} />
            <Tooltip animationDuration={80} cursor={{ stroke: chart.color("border") }} content={<Chart.Tooltip />} />
            <Line isAnimationActive={false} type="monotone" dataKey={chart.key("yaw")} stroke={chart.color("teal.solid")} strokeWidth={2} dot={false} activeDot={false} />
            {activePoint ? (
              <>
                <ReferenceLine x={activePoint.distance} stroke="red" strokeWidth={2} />
                <ReferenceDot x={activePoint.distance} y={activePoint.yaw} r={4} stroke="red" fill="white" strokeWidth={2} />
              </>
            ) : null}
          </LineChart>
        </Chart.Root>
      )}
    </Box>
  );
};

export default LayersChartCZML;
